import type { PropsWithChildren } from 'react';
import React from 'react';

import { PrimaryButtonV3 } from '@fragment/ui/src/components/Button/PrimaryButtonV3/PrimaryButtonV3';
import { SecondaryButtonV3 } from '@fragment/ui/src/components/Button/SecondaryButtonV3/SecondaryButtonV3';
import { TextButton } from '@fragment/ui/src/components/Button/TextButton/TextButton';
import { Icon } from '@fragment/ui/src/components/Icon/BaseIcon/Icon';
import { LayoutRow } from '@fragment/ui/src/components/Payments/LayoutRow';

import { ToastProvider } from '../../wrappers/ToastProvider';

type PaymentViewLayoutProps = {
  title: string;
  primaryAction?: Action;
  secondaryAction?: Action;
  'data-testid'?: string;
  onBack?: () => void;
  backTitle?: string;
};

export type Action = {
  title: string;
  onClick: () => void;
  disabled?: boolean;
  disabledTooltipText?: string;
  'data-testid'?: string;
};

export const kActionButtonWidth = 'w-fit text-xmd';

const ActionButtons = ({
  primaryAction,
  secondaryAction,
}: {
  primaryAction?: Action;
  secondaryAction?: Action;
}) => (
  <div
    className="flex flex-row gap-x-[0.25lh] w-fit justify-between"
    data-testid="invoice-action-buttons"
  >
    {secondaryAction && (
      <SecondaryButtonV3
        onClick={secondaryAction.onClick}
        className={kActionButtonWidth}
        disabled={secondaryAction.disabled}
        disabledTooltipText={secondaryAction.disabledTooltipText}
        data-testid={secondaryAction['data-testid']}
      >
        {secondaryAction.title}
      </SecondaryButtonV3>
    )}
    {primaryAction && (
      <PrimaryButtonV3
        onClick={primaryAction.onClick}
        className={kActionButtonWidth}
        disabled={primaryAction.disabled}
        disabledTooltipText={primaryAction.disabledTooltipText}
        data-testid={primaryAction['data-testid']}
      >
        {primaryAction.title}
      </PrimaryButtonV3>
    )}
  </div>
);

export const PaymentViewLayout = ({
  children,
  title,
  primaryAction,
  secondaryAction,
  onBack,
  backTitle,
  'data-testid': dataTestId,
}: PropsWithChildren<PaymentViewLayoutProps>) => (
  <ToastProvider>
    <div
      className="flex flex-col w-full h-fit min-h-[100dvh] font-sans pb-f2"
      data-testid={dataTestId ?? 'invoice-layout'}
    >
      <div className="flex flex-row w-full h-f8 items-start justify-between border-b-[0.5px] border-main-200">
        {!onBack && (
          <span
            className="text-xmd text-main px-f1 pt-f1"
            data-testid="payment-view-header"
          >
            {title}
          </span>
        )}
        {onBack && (
          <div className="pt-f1 px-f1">
            <TextButton
              onClick={onBack}
              icon={<Icon type="left" size="sm" primaryColor="gray" />}
              hoverIcon={<Icon type="left" size="sm" primaryColor="black" />}
            >
              {backTitle && <span className="font-sans">{backTitle}</span>}
            </TextButton>
          </div>
        )}
        {(primaryAction || secondaryAction) && (
          <div className="flex px-f1 pt-f1">
            <ActionButtons
              primaryAction={primaryAction}
              secondaryAction={secondaryAction}
            />
          </div>
        )}
      </div>
      {children}
    </div>
  </ToastProvider>
);

type HeaderProps = {
  disabled?: boolean;
};

export const PaymentViewHeader = ({
  children,
  disabled,
}: PropsWithChildren<HeaderProps>) => (
  <LayoutRow height="medium" justify="between" hide={disabled}>
    {children}
  </LayoutRow>
);

export const PaymentViewLoading = ({
  message = 'Loading...',
}: {
  message?: string;
}) => (
  <PaymentViewLayout title="Loading...">
    <div className="text-main-500">{message}</div>
  </PaymentViewLayout>
);
